import { z } from 'zod';

// How the merchant's order form looks wherever it opens — in the popup, the cart drawer, its own page or under the
// product — as Shopify's checkout editor styles a checkout: the order button's words and colours, the corners, how
// the fields are drawn, and the reassurance under the button. Empty words and colours keep the store language's
// wording and the theme's own colour, so a store that never opens this looks as it always did.

/** How round the button and the fields are: as the theme draws them, or square, rounded or pill-shaped. */
export const CodFormCornersSchema = z.enum(['THEME', 'SQUARE', 'ROUNDED', 'PILL']);

/** How a field is drawn: a bordered box, or a filled one without a border. */
export const CodFormFieldLookSchema = z.enum(['OUTLINED', 'FILLED']);

/** A colour as #rrggbb, or empty for the theme's own. */
const colour = z.string().trim().regex(/^(#[0-9a-fA-F]{6})?$/, 'Use a colour like #111111').default('');

export const CodFormStyleSchema = z.object({
  buttonText: z.string().trim().max(60, 'Keep the button under 60 characters').default(''),
  // The button that opens the form over a COD page in a popup store ("Commander, paiement à la livraison").
  openButtonText: z.string().trim().max(60, 'Keep the button under 60 characters').default(''),
  showTotalInButton: z.boolean().default(true),
  buttonColor: colour,
  buttonTextColor: colour,
  corners: CodFormCornersSchema.default('THEME'),
  fieldLook: CodFormFieldLookSchema.default('OUTLINED'),
  secureNote: z.string().trim().max(160, 'Keep the note under 160 characters').default(''),
  showSecureNote: z.boolean().default(true)
}).strict();

export type CodFormCorners = z.infer<typeof CodFormCornersSchema>;
export type CodFormFieldLook = z.infer<typeof CodFormFieldLookSchema>;
export type CodFormStyle = z.infer<typeof CodFormStyleSchema>;

/** The look a store has until its merchant changes it: the theme's, as before. */
export const DEFAULT_COD_FORM_STYLE: CodFormStyle = CodFormStyleSchema.parse({});

/** The order button, the note under it, and the button a popup form opens from, as one store language words them. */
export type CodFormDefaultWords = { submitLabel: string; secureNote: string; orderNowLabel: string };

/** What the form prints where Form style leaves the words empty, per store language — the storefront and the admin's
 * placeholders and preview read this one table. */
export const COD_FORM_DEFAULT_WORDS: Readonly<Record<'en' | 'fr' | 'ar', CodFormDefaultWords>> = {
  en: { submitLabel: 'Confirm my order', secureNote: 'Your details are encrypted and used only for this delivery.', orderNowLabel: 'Order now, pay on delivery' },
  fr: { submitLabel: 'Confirmer ma commande', secureNote: 'Vos informations sont chiffrées et servent uniquement à cette livraison.', orderNowLabel: 'Commander, paiement à la livraison' },
  ar: { submitLabel: 'تأكيد الطلب', secureNote: 'معلوماتك مشفرة وتُستعمل فقط لتوصيل هذا الطلب.', orderNowLabel: 'اطلب الآن، الدفع عند الاستلام' }
};

// Reads the default words for a store language ("fr-MA" reads French), English when it is none of the three.
export const codFormDefaultWords = (locale: string): CodFormDefaultWords =>
  COD_FORM_DEFAULT_WORDS[locale.slice(0, 2).toLowerCase() as 'en' | 'fr' | 'ar'] ?? COD_FORM_DEFAULT_WORDS.en;
